import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import StarSVG from "../../images/svg/StarSVG";

export default function TechnicalSkills() {
  const [skills, setSkills] = useState(initSkills);
  const [, setHoveredItemTitle] = useState("");

  const getSkillColor = (rank) => {
    const colors = ["#74C0FC", "#5AABF7", "#3E9EDD", "#1DCB81", "#FDC10E"];
    return colors[rank - 1] || "#FFF";
  };

  return (
    <div className="pointer-events-auto flex-1">
      <div className="flex flex-row items-center text-xs w-full border-b border-[#007bff] transition-all duration-500">
        {skills.map((skill) => (
          <motion.div
            key={skill.id}
            onClick={() => {
              setSkills((prevSkills) =>
                prevSkills.map((curr) => ({
                  ...curr,
                  active: curr.id === skill.id,
                }))
              );
            }}
            style={{
              background: skill.active
                ? "linear-gradient(90deg, #007bff, #6610f2)"
                : "transparent",
              color: skill.active ? "white" : "",
            }}
            whileHover={{ color: skill.active ? "white" : "#007bff" }}
            className="hover:text-[#007bff] cursor-pointer flex-1 px-2 py-2.5 flex items-center justify-center text-[14px]"
          >
            {skill.title}
          </motion.div>
        ))}
      </div>

      <div className="flex flex-col gap-3 transition-all duration-300 min-h-[150px] mt-4">
        <AnimatePresence mode="wait">
          {skills
            .filter((skill) => skill.active)
            .map((skill) => (
              <motion.div
                key={skill.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="tracking-wide flex flex-wrap w-full gap-3"
              >
                {skill.skillArr.map((subskill) => (
                  <motion.div
                    key={subskill.title}
                    style={{
                      color: getSkillColor(subskill.rank),
                      border: `1px solid ${getSkillColor(subskill.rank)}`,
                    }}
                    className="group text-xs font-bold uppercase tracking-widest relative border border-[#6610f2] min-w-[180px] max-w-fit flex-1 text-center min-h-[140px] max-h-[140px]"
                    whileHover={{
                      boxShadow: `0 0 15px ${getSkillColor(subskill.rank)}`,
                    }}
                  >
                    <motion.div
                      className="group-hover:scale-y-0 origin-center transition-all ease-in-out duration-300 delay-100 absolute backdrop-blur-xs bg-black h-2/5 w-full border-y font-bold top-[50%] left-[50%] -translate-x-[50%] z-[1] -translate-y-[50%] flex flex-col items-center justify-center gap-1 text-[10px] pointer-events-none"
                      style={{
                        border: `1px solid ${getSkillColor(subskill.rank)}`,
                        borderRight: "none",
                        fontSize: "10px",
                      }}
                      onHoverStart={() => setHoveredItemTitle(subskill.title)}
                      onHoverEnd={() => setHoveredItemTitle("")}
                      initial={{ opacity: 1 }}
                      whileHover={{ opacity: 0 }}
                    >
                      <div className="text-xs">{subskill.title}</div>
                      <StarSVG
                        rank={subskill.rank}
                        fill={getSkillColor(subskill.rank)}
                      />
                    </motion.div>
                    {!subskill.isVideo ? (
                      <>
                        <img
                          src={subskill.images[0]}
                          className="object-cover object-center h-full w-full opacity-30 blur-[0.5px] hover:blur-0 hover:opacity-100"
                          alt=""
                        />
                      </>
                    ) : (
                      <video
                        className="object-cover object-center h-full w-full opacity-30 blur-[0.5px] hover:blur-0 hover:opacity-100"
                        autoPlay
                        playsInline
                        loop
                        muted
                      >
                        <source src={subskill.images[0]} type="video/webm" />
                      </video>
                    )}
                  </motion.div>
                ))}
              </motion.div>
            ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

const initSkills = [
  {
    id: 1,
    active: true,
    title: "Language",
    color: "#FF0000",
    skillArr: [
      {
        title: "Rust",
        rank: 5,
        images: ["graphbit.png"],
      },
      {
        title: "Javascript",
        rank: 5,
        images: ["projects/myace/1_compressed.webp"],
      },
      {
        title: "Python",
        rank: 5,
        images: ["projects/threebie/1_compressed.webp"],
      },

      {
        title: "C++",
        rank: 5,
        images: ["projects/cpp/1.webm"],
        isVideo: true,
      },
      {
        title: "Typescript",
        rank: 4,
        images: ["projects/lexodi/1_compressed.webp"],
      },
      {
        title: "Go",
        rank: 4,
        images: ["projects/fmgo/1_compressed.webp"],
      },
      {
        title: "Java",
        rank: 3,
        images: ["projects/robinhood/1_compressed.webp"],
      },
      {
        title: "Kotlin",
        rank: 3,
        images: ["projects/memwars/1_compressed.webp"],
      },
    ],
  },
  {
    id: 2,
    active: false,
    title: "Frontend",

    color: "#00FF00",
    skillArr: [
      {
        title: "Nextjs",
        rank: 5,
        images: ["projects/lexodi/2_compressed.webp"],
      },
      {
        title: "Reactjs",
        rank: 5,
        images: ["projects/myace/2_compressed.webp"],
      },
      {
        title: "HTML/CSS",
        rank: 5,
        images: ["projects/brpfc/2_compressed.webp"],
      },
      {
        title: "Flutter",
        rank: 4,
        images: ["projects/error/1_compressed.webp"],
      },

      {
        title: "Vuejs",
        rank: 3,
        images: ["projects/error/1_compressed.webp"],
      },
      {
        title: "Angular",
        rank: 3,
        images: ["projects/error/1_compressed.webp"],
      },
      {
        title: "JavaFx",
        rank: 3,
        images: ["projects/robinhood/2_compressed.webp"],
      },
    ],
  },
  {
    id: 3,
    active: false,
    title: "Backend",

    color: "#0000FF",
    skillArr: [
      {
        title: "Django",
        rank: 5,
        images: ["projects/threebie/4_compressed.webp"],
      },
      {
        title: "Nodejs",
        rank: 5,
        images: ["projects/error/1_compressed.webp"],
      },
      {
        title: "Expressjs",
        rank: 5,
        images: ["projects/error/1_compressed.webp"],
      },
      {
        title: "Nestjs",
        rank: 4,
        images: ["projects/error/1_compressed.webp"],
      },
      {
        title: "Flask",
        rank: 4,
        images: ["projects/kafkastream/1_compressed.webp"],
      },

      {
        title: "Gin",
        rank: 3,
        images: ["projects/fmgo/2_compressed.webp"],
      },
      {
        title: "FastAPI",
        rank: 3,
        images: ["projects/error/1_compressed.webp"],
      },
      {
        title: "Axum",
        rank: 4,
        images: ["projects/error/1_compressed.webp"],
      },
      {
        title: "Amplify",
        rank: 3,
        images: ["projects/error/1_compressed.webp"],
      },
    ],
  },
  {
    id: 4,
    active: false,
    title: "Database",

    color: "#FA00FF",
    skillArr: [
      {
        title: "PostgreSQL",
        rank: 5,
        images: ["projects/threebie/5_compressed.webp"],
      },
      {
        title: "MySQL",
        rank: 5,
        images: ["projects/brpfc/3_compressed.webp"],
      },
      {
        title: "MongoDB",
        rank: 4,
        images: ["projects/kafkastream/3_compressed.webp"],
      },
      {
        title: "Firebase",
        rank: 4,
        images: ["projects/memwars/1_compressed.webp"],
      },

      {
        title: "Redis",
        rank: 4,
        images: ["projects/kafkastream/2_compressed.webp"],
      },
      {
        title: "Neo4j",
        rank: 4,
        images: ["projects/error/1_compressed.webp"],
      },
      {
        title: "Valkey",
        rank: 3,
        images: ["projects/error/1_compressed.webp"],
      },
    ],
  },
  {
    id: 5,
    active: false,
    title: "DevOps",

    color: "#00FAFF",
    skillArr: [
      {
        title: "Linux",
        rank: 5,
        images: ["projects/myace/4_compressed.webp"],
      },
      {
        title: "Docker",
        rank: 5,
        images: ["projects/kafkastream/1_compressed.webp"],
      },
      {
        title: "Jenkins",
        rank: 5,
        images: ["projects/error/1_compressed.webp"],
      },
      {
        title: "Kubernetes",
        rank: 4,
        images: ["projects/error/1_compressed.webp"],
      },
      {
        title: "Nginx",
        rank: 5,
        images: ["projects/brpfc/3_compressed.webp"],
      },
      {
        title: "AWS",
        rank: 4,
        images: ["projects/threebie/1_compressed.webp"],
      },
      {
        title: "Kafka",
        rank: 4,
        images: ["projects/kafkastream/3_compressed.webp"],
      },
    ],
  },
];
